/**
 * Forced password rotation — the activation gate.
 *
 * Reachable ONLY when authenticated but NOT activated. The user MUST set a new
 * password before they can reach any other page. Client-side validation:
 *   - ≥6 characters (ADR-0021; was 12)
 *   - not derivable from the documento (the canonical, any substring, or any
 *     punctuated form)
 *   - passwords match
 *
 * Server-side additionally rejects common passwords and enforces the
 * anti-derivation check in the BFF (cannot be bypassed by the client).
 */
import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isDerivableFromDocumento } from '@my-newshoes/documento';

import { PasswordInput } from '../components/PasswordInput.tsx';
import { useAuth } from '../auth/AuthContext.tsx';
import { IconAlert, IconCheck, IconCircle, IconLock } from '../components/icons.tsx';
import { LocaleSelector } from '../components/LocaleSelector.tsx';

import styles from './RotatePasswordPage.module.css';

// ADR-0021 (was 12). The BFF is the enforcement (password-policy.ts); this
// constant only drives the live checklist — if they disagree, the customer
// sees a green rule and then a server rejection.
const MIN_PASSWORD_LENGTH = 6;

interface Validation {
  tooShort: boolean;
  derivable: boolean;
  doesntMatch: boolean;
}

export function RotatePasswordPage(): React.JSX.Element {
  const auth = useAuth();
  const { t } = useTranslation();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const doc = auth.portalUser?.documento ?? '';

  const validation = useMemo<Validation>(() => {
    const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
    const derivable =
      password.length > 0 && doc.length > 0 && isDerivableFromDocumento(password, doc);
    const doesntMatch = confirm.length > 0 && password !== confirm;
    return { tooShort, derivable, doesntMatch };
  }, [password, confirm, doc]);

  const ok = !validation.tooShort && !validation.derivable && !validation.doesntMatch && password.length > 0 && confirm.length > 0;

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (submitting || !ok) return;

      setSubmitting(true);
      setServerError(null);

      const result = await auth.rotatePassword(password);

      if (result.ok) {
        // Don't navigate manually — AuthProvider's navigation effect picks up
        // the isActivated change and redirects automatically. Navigating here
        // races with the state update and can bounce right back to /rotate.
        setSubmitting(false);
        return;
      } else {
        switch (result.error) {
          case 'too_short':
            setServerError(t('error.passwordTooShort'));
            break;
          case 'derivable_from_documento':
            setServerError(t('error.passwordDerivable'));
            break;
          case 'common_password':
            setServerError(t('error.passwordCommon'));
            break;
          case 'session_expired':
            setServerError(t('error.sessionExpired'));
            break;
          default:
            setServerError(t('error.generic'));
        }
        setSubmitting(false);
      }
    },
    [password, auth, submitting, ok, t],
  );

  return (
    <main className={styles['page']}>
      {/* The gate blocks the app, so this is the only screen a customer who
          picked the wrong language at login can reach. It has to be here. */}
      <div className={styles['locale-bar']}>
        <LocaleSelector />
      </div>

      <header className={styles['header']}>
        <img className={styles['logo']} src="/brand/logo-international-horizontal.png" alt={t('brand.name')} />
        <span className="ns-icon-tile">
          <IconLock size={30} />
        </span>
        <h1 className={`ns-display ${styles['title']}`}>{t('rotate.title')}</h1>
        <p className={styles['subtitle']}>
{t('rotate.subtitle')}
        </p>
      </header>

      <form className={styles['form']} onSubmit={handleSubmit} noValidate>
        <PasswordInput
          label={t('field.newPassword')}
          value={password}
          onChange={setPassword}
          autoFocus
          autoComplete="new-password"
        />

        <PasswordInput
          label={t('field.confirmPassword')}
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
        />

        <ul className={styles['rules']} aria-live="polite">
          <li className={validation.tooShort ? styles['rule-fail'] : password.length >= MIN_PASSWORD_LENGTH ? styles['rule-pass'] : styles['rule-pending']}>
            {validation.tooShort ? <IconAlert size={16} /> : password.length >= MIN_PASSWORD_LENGTH ? <IconCheck size={16} /> : <IconCircle size={16} />}
            {validation.tooShort ? t('rotate.rule.lengthFail') : t('rotate.rule.lengthIdle')}
          </li>
          <li className={validation.derivable ? styles['rule-fail'] : password.length > 0 ? styles['rule-pass'] : styles['rule-pending']}>
            {validation.derivable ? <IconAlert size={16} /> : password.length > 0 ? <IconCheck size={16} /> : <IconCircle size={16} />}
            {validation.derivable ? t('rotate.rule.derivableFail') : t('rotate.rule.derivableIdle')}
          </li>
          <li className={validation.doesntMatch ? styles['rule-fail'] : confirm.length > 0 ? styles['rule-pass'] : styles['rule-pending']}>
            {validation.doesntMatch ? <IconAlert size={16} /> : confirm.length > 0 ? <IconCheck size={16} /> : <IconCircle size={16} />}
            {validation.doesntMatch ? t('rotate.rule.matchFail') : t('rotate.rule.matchIdle')}
          </li>
        </ul>

        {serverError && (
          <div className="ns-alert" role="alert">
            <IconAlert size={18} />
            {serverError}
          </div>
        )}

        <button
          className={`ns-cta ${styles['button']}`}
          type="submit"
          disabled={submitting || !ok}
        >
          {submitting ? t('rotate.submitting') : t('rotate.submit')}
        </button>
      </form>
    </main>
  );
}
